import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of, throwError, timer } from 'rxjs';
import { concatMap, first, map, retry, switchMap, take } from 'rxjs/operators';
import { API_BASE_URL } from '../config/api.config';
import { silentHttpContext } from '../http/http-loading.context';
import {
  AiCreativeDetail,
  AiCreativeListItem,
  AiMarketingAssistantQueryRequest,
  AiMarketingAssistantQueryResponse,
  AiOrganicPost,
  AiPublishDestination,
  PublishOrganicFromCreativeResponse,
  AiShellListResponse,
  MetaChannelStatus,
  MetaConnectChannel,
  MetaInstagramAsset,
  MetaIntegrations,
  MetaOAuthChannel,
  MetaOAuthIntent,
  MetaOAuthUrl,
  MetaPageAsset
} from '../models/ai-marketing.model';

@Injectable({ providedIn: 'root' })
export class AiMarketingService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${API_BASE_URL}/ai-marketing`;

  getIntegrations(): Observable<MetaIntegrations> {
    return this.http.get<MetaIntegrations>(`${this.baseUrl}/integrations`);
  }

  getOAuthUrl(intent: MetaOAuthIntent): Observable<MetaOAuthUrl> {
    let params = new HttpParams();
    if (intent.facebookPage) {
      params = params.set('facebookPage', 'true');
    }
    if (intent.instagram) {
      params = params.set('instagram', 'true');
    }
    return this.http.get<MetaOAuthUrl>(`${this.baseUrl}/connection/oauth-url`, { params });
  }

  disconnect(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/connection`);
  }

  connectFacebookPage(body: {
    facebookPageId: string;
    facebookPageName?: string | null;
    instagramAccountId?: string | null;
    confirmOverwrite?: boolean;
  }): Observable<MetaChannelStatus> {
    return this.http.post<MetaChannelStatus>(`${this.baseUrl}/integrations/facebook-page`, body);
  }

  getPages(channel: MetaConnectChannel = 'facebook'): Observable<MetaPageAsset[]> {
    const params = new HttpParams().set('channel', this.toOAuthChannelParam(channel));
    return this.http.get<MetaPageAsset[]>(`${this.baseUrl}/assets/pages`, { params });
  }

  getInstagram(pageId?: string, channel: MetaConnectChannel = 'facebook'): Observable<MetaInstagramAsset[]> {
    let params = new HttpParams().set('channel', this.toOAuthChannelParam(channel));
    if (pageId) {
      params = params.set('pageId', pageId);
    }
    return this.http.get<MetaInstagramAsset[]>(`${this.baseUrl}/assets/instagram`, { params });
  }

  askAssistant(request: AiMarketingAssistantQueryRequest): Observable<AiMarketingAssistantQueryResponse> {
    return this.http.post<AiMarketingAssistantQueryResponse>(`${this.baseUrl}/assistant/query`, request);
  }

  getCreatives(): Observable<AiShellListResponse<AiCreativeListItem>> {
    return this.http.get<AiShellListResponse<AiCreativeListItem>>(`${this.baseUrl}/creatives`);
  }

  uploadCreative(form: FormData): Observable<AiCreativeDetail> {
    return this.http.post<AiCreativeDetail>(`${this.baseUrl}/creatives/upload`, form);
  }

  createCreativeFromProduct(body: {
    productId: string;
    imageId?: string;
    prompt?: string;
  }): Observable<AiCreativeDetail> {
    return this.http.post<AiCreativeDetail>(`${this.baseUrl}/creatives/from-product`, body);
  }

  approveCreative(id: string): Observable<AiCreativeDetail> {
    return this.withGatewayRetry(
      this.http.post<AiCreativeDetail>(`${this.baseUrl}/creatives/${id}/approve`, {})
    );
  }

  deleteCreative(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/creatives/${id}`);
  }

  getOrganic(): Observable<AiShellListResponse<AiOrganicPost>> {
    return this.http.get<AiShellListResponse<AiOrganicPost>>(`${this.baseUrl}/organic`);
  }

  getPublishDestinations(
    creativeId: string,
    silent = false
  ): Observable<AiShellListResponse<AiPublishDestination>> {
    const params = new HttpParams().set('creativeId', creativeId);
    return this.http.get<AiShellListResponse<AiPublishDestination>>(
      `${this.baseUrl}/organic/publish-destinations`,
      { params, ...(silent ? { context: silentHttpContext } : {}) }
    );
  }

  getOrganicPost(id: string, silent = false): Observable<AiOrganicPost> {
    return this.http.get<AiOrganicPost>(`${this.baseUrl}/organic/${id}`, {
      ...(silent ? { context: silentHttpContext } : {})
    });
  }

  publishOrganicFromCreative(body: {
    creativeId: string;
    caption?: string;
    platform?: string;
    platforms?: string[];
  }): Observable<PublishOrganicFromCreativeResponse> {
    return this.http
      .post<PublishOrganicFromCreativeResponse>(`${this.baseUrl}/organic/publish-creative`, body)
      .pipe(switchMap((result) => this.waitForOrganicPublish(result)));
  }

  private toOAuthChannelParam(channel: MetaConnectChannel): MetaOAuthChannel {
    switch (channel) {
      case 'instagram':
        return 'Instagram';
      case 'all':
        return 'All';
      default:
        return 'FacebookPage';
    }
  }

  private withGatewayRetry<T>(source: Observable<T>): Observable<T> {
    return source.pipe(
      retry({
        count: 2,
        delay: (error, retryCount) => {
          const status = (error as { status?: number })?.status;
          if (status === 0 || status === 502 || status === 504) {
            return timer(2000 * retryCount);
          }
          return throwError(() => error);
        }
      })
    );
  }

  private waitForOrganicPublish(
    result: PublishOrganicFromCreativeResponse
  ): Observable<PublishOrganicFromCreativeResponse> {
    const ids = (result.posts ?? [])
      .filter((p) => p.status === 'Publishing')
      .map((p) => p.id);
    if (ids.length === 0) {
      return of(result);
    }

    return timer(0, 2000).pipe(
      take(46),
      concatMap(() => forkJoin(ids.map((id) => this.getOrganicPost(id, true)))),
      map((posts) => this.mergePublishResult(result, posts)),
      first(
        (current) => !current.posts.some((p) => p.status === 'Publishing'),
        {
          posts: result.posts,
          errors: [
            ...(result.errors ?? []),
            {
              platform: result.posts[0]?.platform ?? 'Instagram',
              error: 'Publish is still running. Check Organic in a minute.'
            }
          ]
        }
      )
    );
  }

  private mergePublishResult(
    original: PublishOrganicFromCreativeResponse,
    posts: AiOrganicPost[]
  ): PublishOrganicFromCreativeResponse {
    const failed = posts
      .filter((p) => p.status === 'Failed')
      .map((p) => ({
        platform: p.platform,
        error: p.errorMessage?.trim() || 'Publish failed.'
      }));
    return {
      posts,
      errors: [...(original.errors ?? []), ...failed]
    };
  }
}
